'use client';

import React from 'react';
import { styled } from '@linaria/react';

import { CURRENT_PROMO, PROMO_ENDS } from '@/constants';
import useInterval from '@/hooks/use-interval';

import TextLink from '@/components/Link/TextLink';
import Superheader, {
  Props as SuperheaderProps,
} from './Superheader';

type Props = Omit<SuperheaderProps, 'superheaderId'>;

function WhimsyLaunchSuperheader(props: Props) {
  const [timeRemaining, setTimeRemaining] = React.useState(() => {
    if (PROMO_ENDS === null) {
      return 0;
    }

    const now = new Date();
    return PROMO_ENDS.getTime() - now.getTime();
  });

  useInterval(
    () => {
      if (PROMO_ENDS === null) {
        return;
      }
      const now = new Date();
      setTimeRemaining(PROMO_ENDS.getTime() - now.getTime());
    },
    timeRemaining > 0 ? 1000 : null
  );

  if (CURRENT_PROMO !== 'wham' || timeRemaining < 0) {
    return null;
  }

  return (
    <Superheader {...props} superheaderId="whimsy-launch">
      <Desktop>
        <TextLink
          href="https://whimsy.joshwcomeau.com/"
          target="_blank"
          rel="noopener"
        >
          My new course, Whimsical Animations, just launched!
        </TextLink>{' '}
        <ExtraText suppressHydrationWarning>
          Sale ends {getRelativeTimeRemaining(timeRemaining)}
        </ExtraText>
      </Desktop>
      <Mobile>
        <TextLink
          href="https://whimsy.joshwcomeau.com/"
          target="_blank"
          rel="noopener"
        >
          “Whimsical Animations” just launched!
        </TextLink>
      </Mobile>
    </Superheader>
  );
}

function getRelativeTimeRemaining(timeRemaining: number) {
  const distanceInSeconds = Math.floor(timeRemaining / 1000);
  const distanceInMinutes = Math.floor(distanceInSeconds / 60);
  const distanceInHours = Math.floor(distanceInMinutes / 60);
  const distanceInDays = Math.floor(distanceInHours / 24);

  let message;

  if (distanceInHours > 24) {
    message = `in ${distanceInDays} day${
      distanceInDays === 1 ? '' : 's'
    }.`;
  } else if (distanceInHours >= 1) {
    message = `in ${distanceInHours} hour${
      distanceInHours === 1 ? '' : 's'
    }.`;
  } else if (distanceInMinutes >= 1) {
    message = `in ${distanceInMinutes} minute${
      distanceInMinutes === 1 ? '' : 's'
    }!`;
  } else {
    message = `in ${distanceInSeconds} second${
      distanceInSeconds === 1 ? '' : 's'
    }!`;
  }

  return message;
}

const Desktop = styled.span`
  display: block;
  @media (max-width: 34rem) {
    display: none;
  }
`;

const Mobile = styled.span`
  display: none;
  @media (max-width: 34rem) {
    display: block;
  }
`;

const ExtraText = styled.span`
  @media (max-width: 48rem) {
    display: none;
  }
`;

export default WhimsyLaunchSuperheader;
